import { t } from '@hefe/i18n';
import { StripePrice, StripeProduct } from '@hefe/models';
import { ModalsSlice, StripeApi } from '@hefe/store';
import { dateFormat, formatDate } from '@hefe/utils';
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Result,
  Row,
  Space,
  Spin,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { environment } from '../../../../../environments/environment';
import { useAppDispatch } from '../../../../store/store';
import styles from './club-subscription-check-update.component.module.scss';

interface ClubSubscriptionCheckUpdateComponentProps {
  product: StripeProduct;
  price: StripePrice;
}

export default function ClubSubscriptionCheckUpdateComponent(
  props: ClubSubscriptionCheckUpdateComponentProps
) {
  const { product, price } = props;

  const dispatch = useAppDispatch();
  const { data, isFetching } = StripeApi.useFetchProrationQuery({
    new_price_id: price.id,
    downgrade: price.update_type === 'switch_next_period',
  });

  const [
    upgradeSubscription,
    { isLoading: isUpgradingSubscription, isSuccess: upgradeWasSuccessful },
  ] = StripeApi.useUpgradeSubscriptionMutation();
  const [
    downgradeSubscription,
    {
      isLoading: isDowngradingSubscription,
      isSuccess: downgradingWasSuccessful,
    },
  ] = StripeApi.useDowngradeSubscriptionMutation();

  const [readAGB, setReadAGB] = useState(false);

  const updateSubscription = () => {
    if (price.update_type === 'switch_next_period') {
      downgradeSubscription({ new_price_id: price.id });
    } else if (price.update_type === 'switch_immidiate') {
      upgradeSubscription({ new_price_id: price.id });
    }
  };

  const prorationData = useMemo(() => {
    if (!data) {
      return;
    }

    const newProductStartsAt = dayjs(data.ends_at)
      .add(1, price.type === 'year' ? 'year' : 'month')
      .format(dateFormat());

    const newPrice = (price.unit_amount / 100).toFixed(2);
    const dueAtIsToday = dayjs(data.due_at).isSame(dayjs(), 'day');

    const subscriptionData = data?.subscription_data?.map((sub) => {
      const amount = (sub.amount / 100).toFixed(2);
      const type = sub.amount < 0 ? 'credit' : 'charge';
      return (
        <div className={styles['amount']}>
          <Typography.Text>
            {t(
              `subscriptions.change_subscription.invoice_preview.calculation.${type}`,
              { product: sub.product_name }
            )}{' '}
            ({formatDate(sub.start_at)} - {formatDate(sub.end_at)})
          </Typography.Text>
          <Typography.Text>{amount}€</Typography.Text>
        </div>
      );
    });

    return (
      <Row
        style={{ backgroundColor: '#f5f5f5', padding: 20, marginBottom: 20 }}
      >
        <Col xs={24}>
          <Typography.Title level={3}>{product.name}</Typography.Title>
          <div className={styles['amount']}>
            <Typography.Text strong>
              {t(
                `subscriptions.change_subscription.invoice_preview.due.afterwards.${price.type}`,
                { date: newProductStartsAt }
              )}
            </Typography.Text>
            <Typography.Text strong>{newPrice}€</Typography.Text>
          </div>
        </Col>
        <Divider style={{ margin: '10px 0' }} />
        <Col xs={24}>
          <div className={styles['amount']}>
            <Typography.Text style={{ fontSize: 20 }} strong>
              {t(
                `subscriptions.change_subscription.invoice_preview.due.next.${
                  dueAtIsToday ? 'today' : 'date'
                }`,
                { date: formatDate(data.due_at) }
              )}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 20 }} strong>
              {data?.total.toFixed(2)}€
            </Typography.Text>
          </div>
        </Col>
        {subscriptionData?.length > 0 && (
          <Col xs={24}>
            <Typography.Paragraph
              strong
              style={{ marginTop: 16, marginBottom: 16 }}
            >
              {t(
                'subscriptions.change_subscription.invoice_preview.calculation.info'
              )}
            </Typography.Paragraph>
            <Space direction="vertical" style={{ width: '100%' }}>
              {subscriptionData}
            </Space>
          </Col>
        )}
      </Row>
    );
  }, [data, price.type, price.unit_amount, product.name]);

  if (isFetching) {
    return <Spin />;
  }

  if (upgradeWasSuccessful || downgradingWasSuccessful) {
    return (
      <Result
        status="success"
        title="Swish!"
        subTitle={t(
          `subscriptions.change_subscription.success.message.${price.update_type}`
        )}
        extra={[
          <Button
            type="primary"
            onClick={() => {
              dispatch(ModalsSlice.modalsActions.closeModal());
              window.location.reload();
            }}
          >
            {t('refresh_page')}
          </Button>,
        ]}
      />
    );
  }

  return (
    <Row>
      <Col xs={24}>
        <Typography.Title level={2}>
          {t(`subscriptions.change_subscription.invoice_preview.title`)}
        </Typography.Title>
        <Typography.Paragraph>
          {t(`subscriptions.change_subscription.invoice_preview.general_info`)}
        </Typography.Paragraph>
        {prorationData}
        <Typography.Paragraph>
          {t(`subscriptions.change_subscription.invoice_preview.card_info`)}
        </Typography.Paragraph>
        <Typography.Paragraph>
          {t(
            `subscriptions.change_subscription.invoice_preview.type_info.${price.update_type}`
          )}
        </Typography.Paragraph>
        <Space direction="vertical" size={12}>
          <Button
            href={environment.frontendUrl + '/agb'}
            type="link"
            target="_blank"
          >
            {t('agb')}
          </Button>
          <Checkbox
            checked={readAGB}
            onChange={(e) => setReadAGB(e.target.checked)}
          >
            {t('subscriptions.change_subscription.checkbox_text')}
          </Checkbox>
        </Space>
      </Col>
      <Col
        xs={24}
        style={{
          textAlign: 'end',
          width: '100%',
          marginTop: 30,
        }}
      >
        <Space size={20}>
          <Button
            onClick={() => dispatch(ModalsSlice.modalsActions.closeModal())}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={() => updateSubscription()}
            disabled={!readAGB}
            loading={isUpgradingSubscription || isDowngradingSubscription}
            type="primary"
          >
            {t('subscriptions.change_subscription.confirm_button')}
          </Button>
        </Space>
      </Col>
    </Row>
  );
}
